import { Client, Company } from 'src/constants';
import { User } from 'src/constants/dataTypes';
import { ENTITIES } from 'src/constants/entityConsts';
import { Events } from 'src/constants/webSocketConsts/events';
import { PortalConfig } from 'src/context';
import { WebSocketMutations } from 'src/hooks/useWebSocketMutations';
import { isContractEntity } from 'src/legacy/components/Contracts/contract_helpers';
import { CustomFieldEntityType } from 'src/store/clients/types';
import { AppDispatch } from 'src/store/configureStore';
import { NotificationGroups } from 'src/store/notifications/types';
import {
  OptimisticUpdateState,
  shouldProcessWebSocketMessage,
} from 'src/store/optimisticUpdates/slice';
import { AppThunkAction } from 'src/store/reduxTypes';
import { Automation } from 'src/types/automations';
import { aiAssistantStatusHandler } from 'src/websocket/aiAssistantStatusHandler';
import {
  AnalyticsEventsAggregateHandler,
  AnalyticsRecentlyVisitedHandler,
} from 'src/websocket/Analytics';
import { handleAppBuilderUpdate } from 'src/websocket/appBuilderHandler';
import ArticleHandler from 'src/websocket/ArticleHandler';
import { handleAuditLogUpdate } from 'src/websocket/auditLogHandler';
import { handleAutomationUpdate } from 'src/websocket/automationHandler';
import { handleAutomationRunsUpdate } from 'src/websocket/AutomationRunHandler';
import channelHandler from 'src/websocket/ChannelHandler';
import { clientCompanySubscriptionHandler } from 'src/websocket/clientCompanySubscriptionHandler';
import clientHandler from 'src/websocket/ClientHandler';
import companyHandler from 'src/websocket/CompanyHandler';
import { customerSubscriptionUpdate } from 'src/websocket/customerSubscriptionHandler';
import { handleCustomFieldsUpdate } from 'src/websocket/CustomFieldsHandler';
import ExtensionItemHandler from 'src/websocket/ExtensionItemHandler';
import fileHandler from 'src/websocket/FileHandler';
import formRequestNotificationHandler from 'src/websocket/FormRequestNotificationHandler';
import FormsTemplatesV2Handler from 'src/websocket/formsTemplatesV2Handler';
import { FormsV2ResponseHandler } from 'src/websocket/FormsV2ResponseHandler';
import { handleInboxNotifications } from 'src/websocket/inboxNotificationHandler';
import {
  handleInternalNotesUpdate,
  isNote,
} from 'src/websocket/internalNotesHandler';
import InternalUserHandler from 'src/websocket/InternalUserHandler';
import { clientCompanyInvoiceHandler } from 'src/websocket/InvoiceCompanyHandler';
import KnowledgeBaseSettingsHandler from 'src/websocket/KnowledgeBaseSettingsHandler';
import { MessageSettingsHandler } from 'src/websocket/MessageSettingsHandler';
import newInvoiceNotificationHandler from 'src/websocket/NewInvoiceNotificationHandler';
import { FileNotificationsHandler } from 'src/websocket/NotificationsHandler';
import { OnboardingTaskStatusHandler } from 'src/websocket/OnboardingTaskStatusHandler';
import paymentHandler from 'src/websocket/paymentHandler';
import PortalSettingsHandler from 'src/websocket/PortalSettingsHandler';
import TagHandler from 'src/websocket/TagHandler';

import { handleContractUpdate } from './contractHandler';
import { handleContractTemplateUpdate } from './contractTemplateHandler';
import { handleInvoiceUpdate } from './invoiceHandler';
import invoiceLegacyHandler from './InvoiceLegacyHandler';
import { handlePaymentLinkUpdate } from './PaymentLinkHandler';
import { handlePriceUpdate } from './priceHandler';
import { handleProductUpdate } from './productHandler';
import { handleSubscriptionUpdate } from './subscriptionHandler';

export const isValidEvent = (event: string): event is Events => {
  const eventsStrings = Object.values(Events).join(',');
  return eventsStrings.includes(event);
};

export interface MsgEvent {
  entity: string;
  event: Events;
  items: Array<any>;
}

function isAutomation(item: any): item is Automation {
  return 'name' in item && 'steps' in item && 'trigger' in item;
}

export const wsMessageHandler = (
  msgEvent: MsgEvent,
  portalConfig: PortalConfig,
  userID: string,
  webSocketMutations: WebSocketMutations,
  dispatch: AppDispatch,
  isClient: boolean,
  optimisticUpdates: OptimisticUpdateState,
  clients: Client[],
  companies: Company[],
  companyClients: User[],
  activeCompanyId: string | null,
): AppThunkAction | { type: string; [key: string]: any } | null | void => {
  const { entity, event, items } = msgEvent;

  // forms response ws item entity string
  // is a combination of the entity name and the formResponseId
  // so we need to check if the entity string includes the form response
  // entity name.
  if (entity.includes(ENTITIES.FORM_RESPONSE)) {
    return FormsV2ResponseHandler(event, items, dispatch);
  }

  // skey for CONTRACT is in format CONTRACT:{contract_template_id}:{unique_uuid}
  // so instead of exact match, we are do inclusion of CONTRACT entity name in skey
  if (isContractEntity(entity)) {
    return handleContractUpdate(event, items, dispatch, {
      isClient,
      activeCompanyId,
    });
  }

  switch (entity) {
    case 'AI_ASSISTANT_STATUS':
      return aiAssistantStatusHandler(event, items, dispatch);
    case ENTITIES.FILE_METADATA:
      return fileHandler(event, items);
    case ENTITIES.STRIPE_INVOICE:
      return invoiceLegacyHandler(event, items);
    case ENTITIES.STRIPE_SUBSCRIPTION:
      return handleSubscriptionUpdate(
        event,
        items,
        dispatch,
        companies,
        isClient ? companyClients : clients,
      );
    case ENTITIES.FILE_CHANNEL:
      return channelHandler(event, items);
    case ENTITIES.CLIENT_USER:
      // Check if we should process this WebSocket message based on mutation counts
      if (
        !shouldProcessWebSocketMessage(
          'CLIENT_USER',
          items,
          dispatch,
          optimisticUpdates,
        )
      ) {
        return null;
      }

      const actionToDispatch = clientHandler(
        event,
        items,
        portalConfig,
        userID,
      );
      if (actionToDispatch) {
        dispatch(actionToDispatch);
      }
      clientCompanySubscriptionHandler(dispatch);
      return clientCompanyInvoiceHandler(isClient, dispatch);
    case ENTITIES.INTERNAL_USER:
      return InternalUserHandler(event, items, portalConfig, userID);
    case ENTITIES.CLIENT_COMPANY:
      return companyHandler(event, items);
    case ENTITIES.PAYMENT_INFO:
      return paymentHandler(event, items);
    case ENTITIES.NOTIFY_NEW_INTERNAL_CHAT_MSG:
      dispatch(handleInboxNotifications(items, event));
      return;
    case ENTITIES.NOTIFY_NEW_INVOICE:
      dispatch(handleInboxNotifications(items, event));
      return newInvoiceNotificationHandler(event, items);
    case ENTITIES.NOTIFY_FILE_UPLOADED:
      dispatch(handleInboxNotifications(items, event));
      return FileNotificationsHandler(
        NotificationGroups.FileNotifications,
        event,
        items,
        // allow marking notification as read only when
        // user is a client
        portalConfig.viewMode === 'client',
      );
    case ENTITIES.NOTIFY_FILE_ESIGN_REQUEST:
      dispatch(handleInboxNotifications(items, event));
      return FileNotificationsHandler(
        NotificationGroups.EsigRequestNotifications,
        event,
        items,
      );
    case ENTITIES.NOTIFY_FILE_ESIGN_REQUEST_COMPLETE:
      FileNotificationsHandler(
        NotificationGroups.EsigRequestCompleteNotifications,
        event,
        items,
      );
      return handleInboxNotifications(items, event);
    case ENTITIES.NOTIFY_FORM_REQUEST: {
      const fnToDispatch = formRequestNotificationHandler(event, items);
      if (fnToDispatch) {
        dispatch(fnToDispatch);
      }
      return handleInboxNotifications(items, event);
    }
    case ENTITIES.NOTIFY_FORM_COMPLETE:
      return handleInboxNotifications(items, event);
    case ENTITIES.FORM_TEMPLATE:
      return FormsTemplatesV2Handler(event, items);
    case ENTITIES.DOMAIN_SETTINGS:
      // invalidate cache for recommended resources on domain connected event, so that refreshed data is fetched when user user go to home page
      webSocketMutations.invalidateRecommendedResources();
      webSocketMutations.setVerifiedDomains(items);
      break;
    case ENTITIES.REFERRAL:
      // update the cache for referral
      webSocketMutations.invalidateReferrals();
      break;
    case ENTITIES.PORTAL_SETTINGS:
      return PortalSettingsHandler(event, items);
    case ENTITIES.MESSAGE_SETTINGS:
      return MessageSettingsHandler(event, items);
    case ENTITIES.CLIENT_CUSTOM_FIELDS:
      return handleCustomFieldsUpdate(
        event,
        items,
        dispatch,
        CustomFieldEntityType.CLIENT,
      );
    case ENTITIES.COMPANY_CUSTOM_FIELDS:
      return handleCustomFieldsUpdate(
        event,
        items,
        dispatch,
        CustomFieldEntityType.COMPANY,
      );
    case ENTITIES.EXTENSION_ITEM:
      return ExtensionItemHandler(event, items);
    case ENTITIES.TAG:
      return TagHandler(event, items);
    case ENTITIES.ARTICLE:
      return ArticleHandler(event, items);
    case ENTITIES.KNOWLEDGE_BASE_SETTINGS:
      return KnowledgeBaseSettingsHandler(event, items);
    case ENTITIES.PORTAL_CONFIG:
      if (portalConfig.updatePortalConfig) {
        // portal config by default does not have update method
        // this gets initialized in App.tsx and passed to websocket handler
        portalConfig.updatePortalConfig(items.at(0).fields);
      }
      break;
    case ENTITIES.NOTIFICATION_GROUPING:
    case ENTITIES.NOTIFY_CUSTOM_APP_ACTION:
    case ENTITIES.NOTIFY_INVOICE_PAID:
    case ENTITIES.NOTIFY_INVOICE_PROCESSING:
    case ENTITIES.NOTIFY_INVOICE_PAYMENT_FAILED:
      return handleInboxNotifications(items, event);
    case ENTITIES.ONBOARDING_TASK_STATUS:
      return OnboardingTaskStatusHandler(items.at(0));
    case ENTITIES.CONTRACT_TEMPLATE:
      return handleContractTemplateUpdate(event, items, dispatch);
    case ENTITIES.APP_BUILDER:
      return handleAppBuilderUpdate(event, items, dispatch);
    case ENTITIES.PRODUCT:
      return handleProductUpdate(event, items, dispatch);
    case ENTITIES.PAYMENTLINK:
      return handlePaymentLinkUpdate(event, items, dispatch);
    case ENTITIES.PRICE:
      return handlePriceUpdate(event, items, dispatch);
    case ENTITIES.INVOICE:
      return handleInvoiceUpdate(event, items, dispatch, companies, clients);
    case ENTITIES.AUTOMATION_RUN:
      return handleAutomationRunsUpdate(event, items, dispatch);
    case ENTITIES.AUTOMATION: {
      items.forEach((automation) => {
        if (isAutomation(automation)) {
          handleAutomationUpdate(event, automation, dispatch);
        }
      });
      return;
    }
    case ENTITIES.RECENTLY_VISITED: {
      return AnalyticsRecentlyVisitedHandler(dispatch);
    }
    case ENTITIES.ANALYTICS_EVENTS_AGGREGATE: {
      return AnalyticsEventsAggregateHandler(items, dispatch);
    }
    case ENTITIES.CUSTOMER_SUBSCRIPTION: {
      return customerSubscriptionUpdate(dispatch);
    }
    case ENTITIES.INTERNAL_NOTES: {
      if (items.every((item) => isNote(item))) {
        handleInternalNotesUpdate(event, items, dispatch);
      }
      return;
    }
    case ENTITIES.AUDIT_LOG_EVENT: {
      handleAuditLogUpdate(event, items, dispatch);
      return;
    }
    default:
      break;
  }
  return null;
};
